import StepWithMaybeResponse from './StepWithMaybeResponse';
import { useContext, useRef, useMemo, useCallback } from 'react';
import { useToggle } from 'react-use-v13';
import { EditableChatMessage } from './EditableChatMessage';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { ResponseTemplatingProps } from './Card';
import { WorkerCommentProps } from 'react-scale/components/scalers/tasks/ThreadedInteraction/feedback/types';
import {
  ChatMessageDisplayType,
  InteractionStepType,
  ReferenceText,
  Attachment,
  MessageRole,
} from '@scale/shared/types/chat';
import { ReviewLevel } from '@scale/shared-types';
import { twMerge } from 'tailwind-merge';
import {
  useChatStoreExtract,
  useTurnByIndex,
  useTurnMessages,
  useTurnContinuationContext,
  useHasIncompleteStepsInTurn,
  useInvalidErrorMessagesForTurn,
  useIsRealCustomerAudit,
  NO_ASSISTANT_RESPONSE_DISCLAIMER,
  NO_ASSISTANT_RESPONSE_NO_VISIBLE_STEPS_DISCLAIMER,
} from '../Chat/hooks/ChatStore.hooks';
import { StepWidthWrapper, StepWidth } from './step/StepWidthWrapper';
import { ExpandableChatMessage } from './ExpandableChatMessage';
import { isStepVisible } from '@scale/shared/chat/api';
import ContributorFeedbackBox from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback';
import {
  visibleStepFilter,
  extractTextFromRegex,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/utils';
import { isPromptEditableAtIndex } from './utils';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

export function CompletedTurn({
  turnIndex,
  totalTurns,
  isAnyTurnIncomplete,
  readOnly,
  onUpdatePrompt,
  workerCommentProps,
  responseTemplatingProps,
  enableRTLText,
  preventScrollToBottom,
}: {
  turnIndex: number;
  totalTurns: number;
  isAnyTurnIncomplete: boolean;
  onUpdatePrompt?: (
    message: string,
    role: MessageRole,
    shouldResetHistory: boolean,
    editedReferenceTexts: ReferenceText[],
    editedAttachments: Attachment[],
    skipPromptInput: boolean,
  ) => Promise<boolean>;
  readOnly?: boolean;
  workerCommentProps?: WorkerCommentProps;
  responseTemplatingProps?: ResponseTemplatingProps;
  userMessageDisplayType?: ChatMessageDisplayType;
  enableRTLText?: boolean;
  preventScrollToBottom?: boolean;
}) {
  const taskContext = useContext(ChatTaskContext);

  // Stores
  const config = useChatStoreExtract(state => state.config);
  const stepParams = useChatStoreExtract(state => state.stepParams);
  const responses = useChatStoreExtract(state => state.responses);
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);
  const turnMessages = useTurnMessages(turnIndex);
  const turnSteps = useTurnByIndex(turnIndex);
  const turnContinuationContext = useTurnContinuationContext(turnIndex);

  const hideSkippedMessages = useUnleashFeatureFlag(
    'chat-v1-hide-skipped-messages',
    { projectId: taskContext.projectId },
    false,
  );

  const hasVisibleTurnStep = useMemo(() => {
    return turnSteps.some(step =>
      visibleStepFilter({
        step,
        responses,
        stepParams,
        reviewLevel,
        templateVariables: config.templateVariables,
      }),
    );
  }, [turnSteps, responses, stepParams, reviewLevel, config.templateVariables]);

  const turnMessageToShow = useMemo(() => {
    const turnMessage =
      turnMessages[0].content === NO_ASSISTANT_RESPONSE_DISCLAIMER && !hasVisibleTurnStep
        ? {
            ...turnMessages[0],
            content: NO_ASSISTANT_RESPONSE_NO_VISIBLE_STEPS_DISCLAIMER,
          }
        : turnMessages[0];
    const turnContent = config.extractModelResponseRegex
      ? extractTextFromRegex(turnMessage.content, config.extractModelResponseRegex)
      : turnMessage.content;
    return {
      ...turnMessage,
      content: turnContent,
    };
  }, [turnMessages, hasVisibleTurnStep, config.extractModelResponseRegex]);

  const hasIncompletedSteps = useHasIncompleteStepsInTurn(turnIndex);
  const invalidErrorMessages = useInvalidErrorMessagesForTurn(turnIndex);
  const isRealCustomerAudit = useIsRealCustomerAudit();
  const isTurnInvalid =
    !isRealCustomerAudit && (hasIncompletedSteps || invalidErrorMessages.length > 0);

  // Other
  const divRef = useRef<HTMLDivElement>(null);

  /*
   * HACK: Whenever config.maxTurns is 1 we want to default to expanded because the UX for
   * for expanding and collapsing is confusing to taskers. We want to ultimately make this
   * expand/collapse UX better for taskers while editing.
   *
   * NOTE: Expanding the turn should only be possible if there are visible steps
   */
  const isSingleTurn = Number(config.maxTurns) === 1;
  const initialExpandedState =
    (isSingleTurn || hasIncompletedSteps || isRealCustomerAudit) && hasVisibleTurnStep;

  const [expanded, toggleExpanded] = useToggle(initialExpandedState);

  const enableRTLTextPromptInput = (
    config.before.find(step => step.type === InteractionStepType.PromptInput) as any
  )?.params.enable_rtl_text;

  const lastResponseIndex = turnSteps.at(-1) ? turnSteps.at(-1)!.responseIndex : undefined;

  const promptAutoCritiqueResponse = turnContinuationContext?.autoCritiqueResponse;

  const stepsKey = useChatStoreExtract(({ stepParams }) =>
    turnSteps
      .map(t => t.responseIndex)
      .filter(index => isStepVisible(stepParams.at(index)!, reviewLevel))
      .join(','),
  );

  const onTurnClick = useCallback(() => {
    // Don't allow hiding the steps if there are incompleted steps
    if (expanded && hasIncompletedSteps) {
      return;
    }

    // Don't allow showing steps if none are visible
    if (!expanded && !hasVisibleTurnStep) {
      return;
    }

    toggleExpanded();
    if (divRef.current) {
      divRef.current.focus();
    }
  }, [expanded, hasIncompletedSteps, hasVisibleTurnStep, toggleExpanded]);

  return (
    <div
      className={twMerge(
        'flex flex-col gap-4',
        taskContext.enableUnifiedFeedback ? 'items-start' : 'items-center',
      )}
      data-completed-steps={stepsKey}
    >
      <div
        tabIndex={-1}
        // For Unified Feedback, the outline and focus ring are handled by `ExpandableChatMessage` such that only the CompletedTurn container
        // is outlined and focused, and not the Unified Feedback feedback box (i.e `ContributorFeedbackBox`) is not included in the outline and and focus.
        className={twMerge(
          'rounded-lg w-full',
          taskContext.enableUnifiedFeedback
            ? 'md:max-w-screen-xl lg:max-w-screen-2xl'
            : 'md:max-w-screen-lg lg:max-w-screen-xl focus:ring-2 focus:ring-primary-100 hover:outline hover:outline-2 hover:outline-primary-200',
          isTurnInvalid && !taskContext.enableUnifiedFeedback
            ? 'outline outline-danger-100 outline-2 hover:outline-danger-500'
            : '',
        )}
        ref={divRef}
      >
        {config.turn.length > 0 && turnMessages.length > 0 && (
          <div className="flex flex-row">
            <StepWidthWrapper width={expanded ? StepWidth.Full : StepWidth.Responsive}>
              <ExpandableChatMessage
                className={twMerge(taskContext.enableUnifiedFeedback && 'max-w-screen-2xl')}
                message={turnMessageToShow}
                onClick={onTurnClick}
                readOnly={readOnly}
                turnIndex={turnIndex}
                expanded={expanded}
                totalTurns={totalTurns}
                isAnyTurnIncomplete={isAnyTurnIncomplete}
                enableRTLText={enableRTLText}
                hideMessageWhenExpanded={
                  !taskContext?.modelResponseConfig?.alwaysShowResponseInPreview
                }
                disableExpand={!hasVisibleTurnStep}
              >
                {turnSteps.map(step => (
                  <StepWithMaybeResponse
                    key={step.responseIndex}
                    responseIndex={step.responseIndex}
                    readOnly={readOnly}
                    preventScrollToBottom={preventScrollToBottom}
                    isCompletedTurnView
                  />
                ))}
              </ExpandableChatMessage>
            </StepWidthWrapper>

            {!expanded && <ContributorFeedbackBox className="ml-4" isHidden />}
          </div>
        )}
      </div>
      {turnMessages
        .slice(1)
        .filter(message => !message.skipMessage || !hideSkippedMessages)
        .map((message, messageIndex) => {
          return (
            <div key={messageIndex} className="flex flex-row">
              <StepWidthWrapper key={messageIndex}>
                <EditableChatMessage
                  key={messageIndex}
                  className="w-full"
                  message={message}
                  onUpdateMessage={onUpdatePrompt}
                  readOnly={
                    !isPromptEditableAtIndex(config, turnIndex + 1) ||
                    reviewLevel === ReviewLevel.ReviewConsensus ||
                    readOnly ||
                    (config.useDefaultUserMessage && !config.defaultUserMessageEditable) ||
                    // only second to last chat message should be editable, because this actually "starts" the last turn
                    (!!config.turnLocking &&
                      !!config.maxTurns &&
                      turnIndex + 1 < Number(config.maxTurns) - 1)
                  }
                  referenceTexts={turnContinuationContext?.referenceTexts}
                  enablePinning={true} // TODO: make this configurable
                  responseIndex={lastResponseIndex}
                  workerCommentProps={workerCommentProps}
                  responseTemplatingProps={responseTemplatingProps}
                  attachments={turnContinuationContext?.attachments}
                  turnIndex={turnIndex + 1}
                  enableRTLText={enableRTLTextPromptInput}
                  autoCritiqueResponse={promptAutoCritiqueResponse}
                  autoFeedbackProps={{
                    childId: `${taskContext.assignmentId ?? taskContext.taskId}-${
                      InteractionStepType.MultiTurnContinue
                    }-${turnIndex + 1}`,
                  }}
                />
              </StepWidthWrapper>
              {turnSteps.at(-1) &&
                turnSteps.at(-1)?.type === InteractionStepType.MultiTurnContinue && (
                  <ContributorFeedbackBox className="ml-4" step={turnSteps.at(-1)} />
                )}
            </div>
          );
        })}
    </div>
  );
}
